/**
 * ArpdauWidget - Виджет ARPDAU (Average Revenue Per Daily Active User)
 * =====================================================================
 * 
 * Показывает график ARPDAU = Total Revenue / DAU
 * Revenue = AdMon + InApps (с учетом платформ)
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { mergeArpdauPeriods } from '../../utils/arpdauPeriods';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { users as usersSpecs } from '@ggstat/chart-specs';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  BarChart3, 
  LineChart as LineChartIcon, 
  AreaChart as AreaChartIcon,
  Users
} from 'lucide-react';

// Цвет для ARPDAU
const ARPDAU_COLOR = usersSpecs.arpdau.series[0].color;

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle, trend, isCurrency = false }) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };
  
  const formatValue = (val) => {
    if (isCurrency) {
      return MetricsService.formatCurrency(val);
    }
    return MetricsService.formatNumber(val);
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold">{formatValue(value)}</p>
              {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          <div className="flex flex-col items-end">
            {getTrendIcon()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Компонент графика ARPDAU
 */
function ArpdauChart({ data, chartType = 'line', groupBy }) {
  // Получаем настройки оси X
  const xAxisConfig = getXAxisConfig(groupBy, data.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      
      // Форматируем дату для tooltip
      const formattedLabel = formatTooltipDate(label, groupBy);
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          <p className="text-sm" style={{ color: ARPDAU_COLOR }}>
            ARPDAU: {MetricsService.formatCurrency(item.arpdau)}
          </p>
          <div className="border-t mt-2 pt-2 space-y-1">
            <p className="text-sm text-muted-foreground">
              Revenue: {MetricsService.formatCurrency(item.totalRevenue)}
            </p>
            <p className="text-sm text-muted-foreground">
              DAU: {MetricsService.formatNumber(item.dau)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  if (chartType === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="arpdau" 
            name="ARPDAU"
            fill={ARPDAU_COLOR}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="arpdau" 
            name="ARPDAU"
            stroke={ARPDAU_COLOR}
            fill={ARPDAU_COLOR}
            fillOpacity={0.3}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  
  // Default: line chart
  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="period"
          tickFormatter={(value) => formatChartDate(value, groupBy)}
          {...xAxisConfig}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          tickFormatter={(value) => MetricsService.formatCurrency(value)}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line 
          type="monotone" 
          dataKey="arpdau" 
          name="ARPDAU"
          stroke={ARPDAU_COLOR}
          strokeWidth={2}
          dot={{ fill: ARPDAU_COLOR, r: 4 }}
          activeDot={{ r: 6 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Основной компонент виджета
 */
export function ArpdauWidget() {
  // Используем localStorage для сохранения настроек
  const [groupBy, setGroupBy] = useLocalStorage('arpdau-widget-groupBy', 'day');
  const [chartType, setChartType] = useLocalStorage('arpdau-widget-chartType', 'line');
  
  // Читаем состояние чекбокса из localStorage
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  // Загружаем данные используя хук.
  // Коэффициенты комиссий и ARPDAU считаются на клиенте (см. useMemo ниже),
  // поэтому shouldApplyFees НЕ входит в зависимости запроса:
  // платформа уже приезжает внутри filters, который useWidgetData слушает сам,
  // а смена чекбокса чистого дохода вообще не требует сети.
  const widgetData = useWidgetData(
    async (filters, options) => {
      // Добавляем календарную фильтрацию
      const calendarFilters = {
        ...filters,
        dateFilterType: 'calendar'
      };

      // Загружаем данные параллельно: DAU и Revenue
      // ARPDAU не показывает loyalActiveUsers — includeLoyal: false срезает
      // uniqExactIf на сервере (917 → 421 мс, план users-section-latency, фаза A)
      // и разводит кэш с DAU/MAU виджетом, который лояльных всё ещё показывает.
      const [activeUsersResponse, admonResponse, inappsResponse] = await Promise.all([
        MetricsService.getActiveUsers(calendarFilters, groupBy, options, { includeLoyal: false }),
        MetricsService.getAdMonByPeriod(calendarFilters, groupBy, options),
        MetricsService.getInAppsByPeriod(calendarFilters, groupBy, options)
      ]);

      // Извлекаем данные DAU
      const dauData = activeUsersResponse?.data || [];

      // API возвращает массив напрямую
      const admonData = Array.isArray(admonResponse) ? admonResponse : [];
      const inappsData = Array.isArray(inappsResponse) ? inappsResponse : [];

      // Склейка ног по периоду. На месяце ключи ног не совпадают буквально
      // ('2026-07' у MAU против '2026-07-01' у выручки) — нормализацию делает
      // mergeArpdauPeriods, см. utils/arpdauPeriods.js. Сырые данные без
      // комиссий и без ARPDAU — их считает useMemo ниже, чтобы клик по
      // чекбоксу комиссий и смена платформы не ходили в сеть повторно.
      return mergeArpdauPeriods({ dauData, admonData, inappsData, groupBy });
    },
    [groupBy]
  );

  // Применяет коэффициенты платформ и считает ARPDAU поверх уже полученных
  // сырых данных — чисто клиентский пересчёт, формулы те же, что были в fetch.
  const arpdauData = React.useMemo(() => {
    const rawData = widgetData.data;
    if (!rawData) return null;

    let data = rawData;

    // Чистый доход: серверные inappsAndroidNet / inappsIosNet вместо gross.
    // Платформенный фильтр уже применён сервером, лишняя платформа в строке — 0.
    data = MetricsService.applyNetRevenue(data, shouldApplyFees);

    // Вычисляем ARPDAU для каждого периода
    data = data.map(item => {
      const totalRevenue = item.admonAndroid + item.admonIos + item.inappsAndroid + item.inappsIos;
      const arpdau = item.dau > 0 ? totalRevenue / item.dau : 0;

      return {
        ...item,
        totalRevenue,
        arpdau
      };
    });

    // Фильтруем периоды где есть и DAU и Revenue
    const validData = data.filter(item => item.dau > 0 && item.totalRevenue > 0);

    // Считаем статистику
    const arpdauValues = validData.map(item => item.arpdau);
    const summary = {
      average: arpdauValues.length > 0
        ? arpdauValues.reduce((a, b) => a + b, 0) / arpdauValues.length
        : 0,
      max: arpdauValues.length > 0 ? Math.max(...arpdauValues) : 0,
      min: arpdauValues.length > 0 ? Math.min(...arpdauValues) : 0,
      totalRevenue: validData.reduce((sum, item) => sum + item.totalRevenue, 0),
      totalDau: validData.reduce((sum, item) => sum + item.dau, 0)
    };

    return {
      data: validData,
      summary
    };
  }, [widgetData.data, shouldApplyFees]);

  // Используем хелпер для BaseWidget, но подменяем data на пересчитанные
  // клиентом ARPDAU/комиссии — loading/error остаются от сырого запроса.
  const widgetProps = { ...useBaseWidget(widgetData), data: arpdauData };
  
  // Получаем название метрики
  const getMetricName = () => {
    switch (groupBy) {
      case 'week': return 'ARPWAU';
      case 'month': return 'ARPMAU';
      default: return 'ARPDAU';
    }
  };
  
  // Компоненты выбора настроек
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily">
          <Calendar className="h-4 w-4 mr-1" />
          Daily
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly">
          <Calendar className="h-4 w-4 mr-1" />
          Weekly
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly">
          <Calendar className="h-4 w-4 mr-1" />
          Monthly
        </ToggleGroupItem>
      </ToggleGroup>
      
      <div className="h-6 w-px bg-border" />
      
      <ToggleGroup type="single" value={chartType} onValueChange={(value) => value && setChartType(value)}>
        <ToggleGroupItem value="line" aria-label="Line Chart">
          <LineChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart">
          <BarChart3 className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart">
          <AreaChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
  
  return (
    <BaseWidget
      {...widgetProps}
      title={`${getMetricName()} Analytics`}
      subtitle={arpdauData?.summary ? `Avg ${getMetricName()}: ${MetricsService.formatCurrency(arpdauData.summary.average)}` : null}
      headerAction={<Controls />}
      emptyMessage="No ARPDAU data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        {/* Статистика */}
        {arpdauData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={DollarSign}
              label={`Average ${getMetricName()}`}
              value={arpdauData.summary.average}
              isCurrency={true}
            />
            <StatCard
              icon={TrendingUp}
              label={`Max ${getMetricName()}`}
              value={arpdauData.summary.max}
              isCurrency={true}
            />
            <StatCard
              icon={TrendingDown}
              label={`Min ${getMetricName()}`}
              value={arpdauData.summary.min}
              isCurrency={true}
            />
            <StatCard
              icon={Users}
              label="Total Periods"
              value={arpdauData.data?.length || 0}
              subtitle={`${groupBy === 'day' ? 'days' : groupBy === 'week' ? 'weeks' : 'months'}`}
            />
          </div>
        )}
        
        {/* График */}
        {arpdauData?.data && arpdauData.data.length > 0 && (
          <ArpdauChart 
            data={arpdauData.data} 
            chartType={chartType}
            groupBy={groupBy}
          />
        )}
      </div>
    </BaseWidget>
  );
}

export default ArpdauWidget;
